import React, { useEffect, useRef, useState } from 'react';

// Generate-Week's result: how each day was filled, second chances, and what needs a person (subs with nothing to post, subs
// that now require verification, a week the approved subs can't carry).
const TIERS = [
  { key: 'normal', emoji: '📌', label: 'In rotation', hint: 'Approved subs, at the pace their audience allows', color: '#3b82f6' },
  { key: 'second_chance', emoji: '🔁', label: 'Second chances', hint: 'Subs dropped earlier: 3 posts each', color: '#14b8a6' },
  { key: 'extra', emoji: '➕', label: 'Extra posts', hint: 'Filled short days in subs whose audience sees us least', color: '#f59e0b' }
];
const EMPTY_COLOR = '#2e2e33';

// "Sat 26 Sep" (the dates are UTC days)
const dayLabel = (date) => {
  const d = new Date(`${date}T00:00:00Z`);
  const part = (opts) => d.toLocaleDateString('en-US', { ...opts, timeZone: 'UTC' });
  return `${part({ weekday: 'short' })} ${part({ day: 'numeric' })} ${part({ month: 'short' })}`;
};
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

function SummaryTile({ tier, count }) {
  return (
    <div title={tier.hint} style={{ flex: '1 1 110px', padding: '10px 12px', background: '#141418', borderRadius: '10px', borderTop: `3px solid ${tier.color}`, opacity: count ? 1 : 0.45 }}>
      <div style={{ fontSize: '20px', fontWeight: 700, color: '#fff' }}>{count}</div>
      <div style={{ fontSize: '12px', color: '#aaa', marginTop: '2px' }}>{tier.emoji} {tier.label}</div>
    </div>
  );
}

// One segment per tier, widths out of the day's target; unfilled slots grey
function DayBar({ session }) {
  const target = Math.max(session.target || 0, session.sub_count || 0, 1);
  const empty = Math.max(0, target - (session.sub_count || 0));
  return (
    <div style={{ display: 'flex', height: '10px', borderRadius: '5px', overflow: 'hidden', background: EMPTY_COLOR, flex: 1 }}>
      {TIERS.map(t => session[t.key] > 0 && (
        <div key={t.key} title={`${session[t.key]} ${t.label.toLowerCase()}`} style={{ width: `${(session[t.key] / target) * 100}%`, background: t.color }} />
      ))}
      {empty > 0 && <div title={`${plural(empty, 'empty slot')}`} style={{ width: `${(empty / target) * 100}%` }} />}
    </div>
  );
}

function SubChip({ name, color }) {
  return (
    <span style={{ fontSize: '12px', color: '#ddd', padding: '3px 9px', borderRadius: '999px', background: `${color}1f`, border: `1px solid ${color}55`, whiteSpace: 'nowrap' }}>{name}</span>
  );
}

function DayRow({ session }) {
  const [open, setOpen] = useState(false);
  const full = session.sub_count >= session.target;
  return (
    <div style={{ borderBottom: '1px solid #232327' }}>
      <button onClick={() => setOpen(o => !o)} aria-expanded={open}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '12px', padding: '9px 2px', background: 'none', border: 'none', cursor: 'pointer', color: '#ddd', textAlign: 'left' }}>
        <span style={{ width: '88px', fontSize: '13px', fontWeight: 600 }}>{dayLabel(session.date)}</span>
        <DayBar session={session} />
        <span style={{ width: '52px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: full ? '#22c55e' : '#f59e0b' }}>{session.sub_count}/{session.target}</span>
        <span style={{ width: '14px', fontSize: '10px', color: '#666' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', padding: '2px 0 12px 100px' }}>
          {TIERS.flatMap(t => session.subs.filter(s => s.tier === t.key).map(s => <SubChip key={`${t.key}-${s.name}`} name={s.name} color={t.color} />))}
        </div>
      )}
    </div>
  );
}

function Section({ emoji, title, hint, tone = 'plain', children }) {
  const warn = tone === 'warn';
  return (
    <div style={{ marginTop: '14px', padding: warn ? '12px 14px' : 0, background: warn ? '#221a0b' : 'none', borderRadius: '10px', border: warn ? '1px solid #92400e' : 'none' }}>
      <div style={{ fontSize: '14px', fontWeight: 600, color: warn ? '#fbbf24' : '#fff' }}>{emoji} {title}</div>
      {hint && <div style={{ fontSize: '12px', color: warn ? '#d6b98a' : '#888', margin: '3px 0 8px', lineHeight: 1.5 }}>{hint}</div>}
      {children}
    </div>
  );
}

const SHOWN_AT_FIRST = 8;
// Subs with a note each: [{ id, name, need, planned? }]
function SubList({ items }) {
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, SHOWN_AT_FIRST);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
      {shown.map(s => (
        <div key={s.id} style={{ fontSize: '12px', color: '#ddd' }}>
          <span style={{ fontWeight: 600 }}>{s.name}</span><span style={{ color: '#b89a6a' }}> — {s.need}{s.planned ? ` · ${plural(s.planned, 'post')} planned` : ''}</span>
        </div>
      ))}
      {items.length > SHOWN_AT_FIRST && (
        <button onClick={() => setAll(a => !a)} style={{ alignSelf: 'flex-start', marginTop: '4px', background: 'none', border: 'none', padding: 0, color: '#fbbf24', cursor: 'pointer', fontSize: '12px' }}>
          {all ? 'Show fewer' : `Show all ${items.length}`}
        </button>
      )}
    </div>
  );
}

// The Karma farming section: comments only (under the comment karma target, or
// past it with nothing SFW to post in yet), or comments plus SFW posts
function karmaHeading(karma, noPosts) {
  if (noPosts) return { title: `No posting sessions — ${noPosts.why}`, tone: 'warn', hint: `Comments only (see the Scheduler's comments panel). Next: ${noPosts.next}` };
  if (karma.stage === 'comments') return { title: 'Comments only for now', hint: `Comment karma ${karma.max_comment ?? 'not read yet'} of the ${karma.target} target. Until it gets there nothing is posted; then the biggest SFW subs a fresh account can post to join the list by themselves.` };
  const ramp = (karma.posts_a_day || []).join(', ');
  return { title: 'Comments stop, SFW posts start', hint: `The comment karma target is reached: no more comments; ${ramp ? `${ramp} posts a day` : 'posts'} (1, 2, 3, 4, then 5 a day from the first posting day) in big SFW subs (image-only subs first), drawn at random. The VA makes each post: content that fits the sub, not the model's vault.` };
}

const chipRow = (names, color) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>{names.map(n => <SubChip key={n} name={n} color={color} />)}</div>
);

export default function WeekResultModal({ result, username, onClose }) {
  const closeRef = useRef(null);
  // The parent re-renders while the window is open (the sessions list
  // reloads): focus once, and Escape always calls the latest onClose
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onCloseRef.current(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const sessions = result.sessions || [];
  const tiers = result.tiers || {};
  const first = sessions[0], last = sessions[sessions.length - 1];
  const room = result.room || {};
  const nothingFits = result.nothing_fits || [];
  const unverified = result.unverified_subs || [];
  const skipped = result.skipped_off_strategy || [];
  const otherAccount = result.other_account_subs || [];
  const recentlyRemoved = result.recently_removed_subs || [];
  const karmaGated = result.skipped_karma || [];
  // Karma farming (backend/helpers/karmaFarming.js)
  const karma = result.karma || null;
  const commentPlans = result.comment_plans || [];
  const noPosts = result.no_posts || null;
  const commentsOnly = karma && (karma.stage === 'comments' || !!noPosts);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1003, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div role="dialog" aria-modal="true" aria-labelledby="week-result-title" onClick={e => e.stopPropagation()}
        style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '14px', width: '100%', maxWidth: '640px', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}>

        <div style={{ padding: '20px 22px 14px', borderBottom: '1px solid #262626' }}>
          <div id="week-result-title" style={{ fontSize: '18px', fontWeight: 700, color: '#fff' }}>✅ Week planned for u/{username}</div>
          <div style={{ fontSize: '13px', color: '#999', marginTop: '4px' }}>
            {commentsOnly
              ? `Karma farming · ${commentPlans.length ? `${plural(commentPlans.reduce((n, p) => n + (p.planned || 0), 0), 'comment')} over ${plural(commentPlans.length, 'day')}` : 'no new comment plans'}`
              : <>{first && last ? `${dayLabel(first.date)} → ${dayLabel(last.date)} · ` : ''}{plural(result.total_posts || 0, 'post')} in {plural(result.sessions_created || 0, 'session')}{result.include_profile_post ? ' · plus a profile post each day' : ''}</>}
          </div>
        </div>

        <div style={{ padding: '14px 22px', overflowY: 'auto' }}>
          {karma && (
            <Section emoji="💬" {...karmaHeading(karma, noPosts)}>
              {commentPlans.length > 0 ? commentPlans.map(p => (
                <div key={p.id || p.plan_date} style={{ fontSize: '12px', color: '#bbb', padding: '2px 0' }}>
                  {dayLabel(p.plan_date)}: {plural(p.planned || 0, 'comment')} — {(p.subs || []).map(x => `${x.name} ×${x.count}`).join(' · ')}
                </div>
              )) : karma.stage === 'comments' ? <div style={{ fontSize: '12px', color: '#777' }}>These days already had their comment plans.</div> : null}
              {(karma.subs_added || []).length > 0 && (
                <div style={{ marginTop: '8px' }}>
                  <div style={{ fontSize: '12px', color: '#bbb', marginBottom: '4px' }}>{plural(karma.subs_added.length, 'SFW sub')} added to the list, approved (image-only subs first, then biggest weekly visitors; no karma / age / verification rules):</div>
                  {chipRow(karma.subs_added, '#22c55e')}
                </div>
              )}
            </Section>
          )}
          {commentsOnly ? null : <>
          <div style={{ fontSize: '12px', color: '#777', marginBottom: '6px' }}>Where the {plural(result.total_posts || 0, 'post')} go</div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {TIERS.map(t => <SummaryTile key={t.key} tier={t} count={tiers[t.key] || 0} />)}
          </div>
          {!(room.short_by > 0) && (
            <div style={{ fontSize: '12px', color: '#22c55e', marginTop: '10px' }}>
              ✅ Your approved subs have room for {room.per_week} of the {room.target_per_week} posts a week at the pace their audiences allow
            </div>
          )}

          <div style={{ marginTop: '16px', fontSize: '12px', color: '#777' }}>Days (tap one to see its subs)</div>
          <div>{sessions.map(s => <DayRow key={s.id || s.date} session={s} />)}</div>

          {room.short_by > 0 && (
            <Section emoji="🔍" title="New subreddits are needed" tone="warn"
              hint={`At the pace their audiences allow, your approved subs have room for ${room.per_week} of the ${room.target_per_week} posts a week. The rest went to second chances and extra posts (never more than twice a sub's usual pace)${tiers.empty > 0 ? `, and ${plural(tiers.empty, 'slot')} stayed empty` : ''}. ${room.new_subs_needed ? `Add about ${plural(room.new_subs_needed, 'sub')}, mostly mid-size (100–300K weekly visitors): approve Pending subs or run Discovery from the Planner.` : 'Approve Pending subs or run Discovery from the Planner.'}`} />
          )}
          {!(room.short_by > 0) && tiers.empty > 0 && (
            <Section emoji="⬜" title={`${plural(tiers.empty, 'slot')} stayed empty`} tone="warn"
              hint="No sub could take another post on those days: an extra post never comes sooner than half a sub's usual wait. Approve more subs in the Planner (Pending subs are never planned until you approve them)." />
          )}

          {nothingFits.length > 0 && (
            <Section emoji="🖼️" title={`Nothing in the vault fits ${plural(nothingFits.length, 'sub')}`} tone="warn"
              hint={`Auto-fill leaves their ${plural(nothingFits.reduce((n, s) => n + (s.planned || 0), 0), 'slot')} empty for you to pick by hand. Add or label photos that fit, or remove subs you don't want — the Planner has a card listing them.`}>
              <SubList items={nothingFits} />
            </Section>
          )}

          {otherAccount.length > 0 && (
            <Section emoji="👥" title={`${plural(otherAccount.length, 'sub')} left out: another account of this model uses ${otherAccount.length === 1 ? 'it' : 'them'}`} tone="warn"
              hint="This is a second account: it stays out of the main account's subs and of subs run by the same moderators. The Planner has a card to remove them from this account's list.">
              <SubList items={otherAccount.map(s => ({ id: s.id, name: s.name, need: s.why }))} />
            </Section>
          )}
          {recentlyRemoved.length > 0 && (
            <Section emoji="↩️" title={`${plural(recentlyRemoved.length, 'sub')} planned again after a recent removal`} tone="warn"
              hint="Someone accepted a Remove card for these under 30 days ago and they are approved again, so the plan includes them. Meant? Fine. Not meant? Remove them in the Planner — its log shows who approved them.">
              <SubList items={recentlyRemoved.map(s => ({ id: s.id, name: s.name, need: `removed on ${s.removed_on}` }))} />
            </Section>
          )}
          {result.account_role === 'undecided' && (
            <Section emoji="👥" title={`${result.model_name} has no main account yet`} tone="warn"
              hint="Subs its accounts share were planned anyway and are only flagged. Choose the main account in the account's strategy (🎯 on the Accounts page), then the second accounts stay out of its subs." />
          )}

          {unverified.length > 0 && (
            <Section emoji="🔒" title={`${plural(unverified.length, 'sub')} now require${unverified.length === 1 ? 's' : ''} verification`} tone="warn"
              hint={unverified.length === 1
                ? "This account isn't verified there, so those posts will likely be removed. Verify it there, or accept its Verify card in the Planner (the scheduler then skips it until it's verified)."
                : "This account isn't verified there, so those posts will likely be removed. Verify them there, or accept their Verify cards in the Planner (the scheduler then skips them until they're verified)."}>
              {chipRow(unverified, '#f59e0b')}
            </Section>
          )}

          {(result.second_chance_subs || []).length > 0 && (
            <Section emoji="🔁" title={`Second chances (${result.second_chance_subs.length})`} hint="Subs dropped earlier get 2 posts; a Bring back card comes if they did well">
              {chipRow(result.second_chance_subs, TIERS[1].color)}
            </Section>
          )}

          {skipped.length > 0 && (
            <Section emoji="🎯" title={`${plural(skipped.length, 'sub')} skipped: outside the strategy`} hint={skipped.map(s => s.name).join(', ')} />
          )}
          {karmaGated.length > 0 && (
            <Section emoji="🔐" title={`${plural(karmaGated.length, 'sub')} skipped: the account doesn't have the karma yet`}
              hint={`Their rules ask for more karma than the account has, so posts there would be removed. They come back by themselves as its karma grows: ${karmaGated.map(s => `${s.name} (${s.needs})`).join(', ')}`} />
          )}
          </>}
        </div>

        <div style={{ padding: '12px 22px', borderTop: '1px solid #262626', display: 'flex', justifyContent: 'flex-end' }}>
          <button ref={closeRef} onClick={onClose}
            style={{ padding: '8px 22px', borderRadius: '8px', border: 'none', background: '#ff4500', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}>Done</button>
        </div>
      </div>
    </div>
  );
}
